import React from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import Grid from "@mui/material/Grid";
import { useLazyQuery } from "@apollo/client/react";
import { ASSETS_COLLECTION_TABLE_QUERY } from "../../../constants/graphql";

import Autocomplete from "@mui/material/Autocomplete";
import Popper from "@mui/material/Popper";
import TextField from "@mui/material/TextField";
import Input from "../../../components/form/Input";

const validationSchema = Yup.object({
  field: Yup.string().required("Required"),
  findText: Yup.string().required("Required"),
});

function BottomAnchoredPopper(popperProps) {
  return (
    <Popper
      {...popperProps}
      placement="bottom-start"
      modifiers={[
        { name: "flip", enabled: false },
        { name: "preventOverflow", enabled: false },
        { name: "offset", options: { offset: [0, 8] } },
      ]}
    />
  );
}

export default function FindModal({ open, onClose, fields, onFindComplete }) {
  const [currentValues, setCurrentValues] = React.useState({ field: "", findText: "" });

  const [searchAssetsQuery, { loading: searchLoading }] = useLazyQuery(ASSETS_COLLECTION_TABLE_QUERY, {
    fetchPolicy: "network-only",
  });
  const searchAssets = (options) =>
    searchAssetsQuery(options).then(({ data }) => {
      const results = data?.assetsCollection?.collection || [];
      onFindComplete(results, currentValues.field, currentValues.findText);
    });

  const handleSubmit = values => {
    setCurrentValues({ field: values.field, findText: values.findText });

    const searchQuery = `${values.field} : ${values.findText}`;
    searchAssets({
      variables: {
        search: searchQuery,
      },
    });
  };

  const filteredFields = fields
    .filter(
      f =>
        f.visible &&
        !f.unEditable &&
        f.type !== "checkbox" &&
        f.type !== "photo" &&
        !f.disableFillDown
    )
    .filter(f => !["itemId", "photoCarousel", "rackLocations"].includes(f.id));

  const fieldSuggestions = filteredFields.map(f => ({ id: f.id, name: f.label }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography fontSize={24} fontWeight={500} color="textPrimary">
          Find and Replace
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Formik
        initialValues={{ field: "", findText: "" }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, handleSubmit, values, setFieldValue }) => (
          <>
            <DialogContent>
              <Box mt={2}>
                <Grid container spacing={2}>
                  <Grid size={12}>
                    <Input name="findText" label="Find" size="small" showNAButton={false} />
                  </Grid>
                  <Grid size={12}>
                    <Autocomplete
                      fullWidth
                      PopperComponent={BottomAnchoredPopper}
                      options={fieldSuggestions}
                      getOptionLabel={option => option.name || ""}
                      value={fieldSuggestions.find(f => f.id === values.field) || null}
                      onChange={(event, newValue) =>
                        setFieldValue("field", newValue ? newValue.id : "")
                      }
                      isOptionEqualToValue={(option, val) => option.id === val?.id}
                      ListboxProps={{
                        style: {
                          maxHeight: "300px",
                          overflow: "auto",
                        },
                      }}
                      filterOptions={(options, { inputValue }) => {
                        if (!inputValue) return options;
                        const term = inputValue.toLowerCase();
                        return options.filter(o => (o?.name || "").toLowerCase().includes(term));
                      }}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label="Search Within"
                          name="field"
                          variant="outlined"
                          size="small"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Box>
            </DialogContent>
            <DialogActions>
              <Box display="flex" justifyContent="flex-end" width="100%" p={2}>
                <Box display="flex" gap={1}>
                  <Button variant="outlined" onClick={onClose} color="primary">
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={isSubmitting || searchLoading}
                    startIcon={searchLoading && <CircularProgress size={22} />}
                  >
                    Find
                  </Button>
                </Box>
              </Box>
            </DialogActions>
          </>
        )}
      </Formik>
    </Dialog>
  );
}
